import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';

// Components
import { UsersManagementTable } from '../../../../components/display/tables/formTables/managementTables/UsersManagementTable';
import { AddUserForm } from '../../../../components/forms/AddUserForm';

// Types
import { UserManagement } from '../../../../components/forms/AddUserForm/types';
import { UserManagementLoader } from './types';

// Styles
import { Container, UserManagementSection } from './styles';

// Constants
import { formUserManagementNaming } from '../../../../components/forms/AddUserForm/constants';

// Hooks
import { useAuth } from '../../../../hooks/useAuth';

// GQL
import { client } from '../../../../gql/client';
import {
  CREATE_APP_USER,
  CREATE_OPERATOR,
  UPDATE_USER_STATE,
} from '../../../../gql/mutations/auth';
import { NEW_USER } from '../../../../gql/fragments/users';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';

export default function UsersManagementPage(): JSX.Element {
  const loaderData = useAsyncValue() as UserManagementLoader;
  const revalidator = useRevalidator();
  const userData = useAuth();

  const allUsersEmails = [
    ...loaderData.activeUsers,
    ...loaderData.inactiveUsers,
  ].map((user) => user.email);

  const onReFetchData = useCallback(() => {
    revalidator.revalidate();
  }, []);

  const onFormSubmit = async (data: UserManagement) => {
    // Check email uniqueness (except for operators who might not have email)
    if (
      data.rolActive !== 'operating-team' &&
      allUsersEmails.some((email) => email === data.email)
    ) {
      return "L'email ja existeix";
    }

    let concessionRelationships;

    if (data.concession === 'all') {
      const {
        data: { concessions },
      } = await client.query({
        query: GET_CONCESSIONS,
      });
      concessionRelationships = concessions.map(
        (concesion: { id: string }) => concesion.id
      );
    } else {
      concessionRelationships = [data.concession];
    }

    // Determine if it's an operator or app-user based on rolActive
    const isOperator = data.rolActive === 'operating-team';

    // TenantId is obtained automatically from authenticated user in backend

    // Use appropriate mutation based on userType
    if (isOperator) {
      // Create operator (no email required)
      await client.mutate({
        mutation: CREATE_OPERATOR,
        variables: {
          input: {
            name: data.name.trim(),
            surName: data.surName.trim(),
            isActive: true,
            concessionRelationships,
            teamRelationships: data.teamRelationships || [],
            internal: true, // DEPRECATED: retrocompatibilidad, se asume siempre true
          },
        },
        update(cache, { data: { signUpOperator } }) {
          cache.modify({
            fields: {
              users(existingUsers = []) {
                const newUserRef = client.writeFragment({
                  fragment: NEW_USER,
                  data: signUpOperator,
                });
                return [...existingUsers, newUserRef];
              },
            },
          });
        },
      });
    } else {
      // Create app-user (email required)
      await client.mutate({
        mutation: CREATE_APP_USER,
        variables: {
          input: {
            email: data.email.trim(),
            name: data.name.trim(),
            surName: data.surName.trim(),
            rolActive: data.rolActive,
            isActive: true,
            concessionRelationships,
            internal: true, // DEPRECATED: retrocompatibilidad, se asume siempre true
          },
        },
        update(cache, { data: { signUpAppUser } }) {
          cache.modify({
            fields: {
              users(existingUsers = []) {
                const newUserRef = client.writeFragment({
                  fragment: NEW_USER,
                  data: signUpAppUser,
                });
                return [...existingUsers, newUserRef];
              },
            },
          });
        },
      });
    }

    onReFetchData();
  };

  const onAddActiveUser = async (userId: string, userEmail?: string) => {
    await client.mutate({
      mutation: UPDATE_USER_STATE,
      variables: {
        input: {
          id: userId,                    // Primary identifier (works for all users)
          email: userEmail || undefined, // Optional: undefined for operators without email
          isActive: false,
        },
      },
    });

    onReFetchData();
  };

  const onAddInactiveUser = async (userId: string, userEmail?: string) => {
    await client.mutate({
      mutation: UPDATE_USER_STATE,
      variables: {
        input: {
          id: userId,                    // Primary identifier (works for all users)
          email: userEmail || undefined, // Optional: undefined for operators without email
          isActive: true,
        },
      },
    });

    onReFetchData();
  };

  return (
    <Container>
      <UserManagementSection>
        <AddUserForm
          initialData={loaderData.cacheData}
          onFormSubmit={onFormSubmit}
          namingPersistForm={formUserManagementNaming.NEW_USER_MANAGEMENT_FORM}
        />
      </UserManagementSection>
      <UserManagementSection>
        <UsersManagementTable
          onAddActiveUser={onAddActiveUser}
          onAddInactiveUser={onAddInactiveUser}
          data={{
            activeUsers: loaderData.activeUsers,
            inactiveUsers: loaderData.inactiveUsers,
          }}
        />
      </UserManagementSection>
    </Container>
  );
}
